/* DEVELOPER NOTE:
	Migration port-forward (Select2 -> pict-section-picker): the "BridgeSelector" input type now renders via
	the "Picker" stack across ALL themes. EDIT attaches the Picker provider; READ / PRINT attach
	PickerDisplay. Every theme includes the matching Picker template by reference (single source of the
	picker markup).
	This is a SPECIALIZED selector: its Entity (Bridge) is hardcoded in its provider, NOT the
	descriptor, so the broker infers it from the InputType via _SPECIALIZED_SELECTOR_MAP (see
	PictProvider-InputType-Picker). The field keeps InputType "BridgeSelector" in the manifest -- no manifest
	edit is needed; PickerDisplay resolves the entity name (+ EntityTag badge) for the Read/Print display.
*/

const _TemplatesGroupedByTheme =
{
	/* EDIT THEME - Interactive mode for browsers */
	Edit: [
		{
			HashPostfix: '-Template-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'Picker' ],
			// BESPOKE, not a bare reference: it reproduces the Picker Edit chrome and keeps the legacy Bridge
			// Search Panel buttons, which the truth template dropped — losing them orphans the panel, whose
			// selection callback the broker still wires (_wireBridgeSearchPanel). Two buttons, as legacy: a
			// labelled one on mobile and an icon-only one above it. No Show All toggle, because the legacy
			// BridgeSelector had none and its customer-state scope is mandatory.
			Template: /*HTML*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (+ bridge panel addon) -->
				<div class="column {~ColumnWidth:Record~}">
					<input
						id="MATERIALS-ENTITY-TRACKING-GUID-{~D:Record.Macro.RawHTMLID~}"
						type="hidden"
						tabindex="-1"
						value=""
					/>
					<input
						id="{~D:Record.Macro.RawHTMLID~}"
						type="hidden"
						tabindex="-1"
						{~D:Record.Macro.InputFullProperties~}
						{~D:Record.Macro.InputChangeHandler~}
						{~Required:Record~}
						data-validation-surface="PICKER-FOR-{~D:Record.Macro.RawHTMLID~}"
						value=""
					/>
					<div class="field">
						<label class="label {~HideLabel:Record~}">{~LabelWithChips:Record~}</label>
						<div class="field has-addons has-addons-right mb-0">
							<p class="control control-status is-expanded">
								<span class="control-status-icon"></span>
								<span id="PICKER-FOR-{~D:Record.Macro.RawHTMLID~}" class="pps-form-host" {~D:Record.Macro.ControlAttr~}></span>
							</p>
							<p class="control is-hidden-tablet is-fullwidth">
								<button class="button is-link addons-stack-mobile" type="button" onclick="_Pict.views.HeadlightPaneBridges.openBridgePanelFromAdvancedButton(event)">
									<span class="icon is-small">
										<i class="fas fa-bars-filter"></i>
									</span>
									<span>Search Bridges</span>
								</button>
							</p>
							<p class="control is-hidden-mobile">
								<button class="button is-link" type="button" onclick="_Pict.views.HeadlightPaneBridges.openBridgePanelFromAdvancedButton(event)">
									<span class="icon is-small">
										<i class="fas fa-bars-filter"></i>
									</span>
								</button>
							</p>
						</div>
						<p class="help"><span class="description-text">{~D:Record.Description~}</span></p>
					</div>
				</div>`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'Picker' ],
			Template: /*HTML*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker -->
				{~T:PictProvider-Theme-Edit-VerticalTemplate-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'Picker' ],
			Template: /*HTML-Partial*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker -->
				{~T:PictProvider-Theme-Edit-TabularTemplate-Begin-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'Picker' ],
			Template: /*HTML-Partial*/`
				{~T:PictProvider-Theme-Edit-TabularTemplate-End-Input-InputType-Picker~}`,
		},
	],
	/* READ THEME - forwarded to the Picker display: PickerDisplay resolves the value into the shared
	   PICKER-DISPLAY node the Picker's own Read templates render. */
	Read: [
		{
			HashPostfix: '-Template-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (display) -->
				{~T:PictProvider-Theme-Read-Template-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (display) -->
				{~T:PictProvider-Theme-Read-VerticalTemplate-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML-Partial*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (display) -->
				{~T:PictProvider-Theme-Read-TabularTemplate-Begin-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML-Partial*/`
				{~T:PictProvider-Theme-Read-TabularTemplate-End-Input-InputType-Picker~}`,
		},
	],
	/* PRINT THEME - forwarded to the Picker display (see READ). */
	Print: [
		{
			HashPostfix: '-Template-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (display) -->
				{~T:PictProvider-Theme-Print-Template-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (display) -->
				{~T:PictProvider-Theme-Print-VerticalTemplate-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML-Partial*/`
				<!-- Provided InputType: BridgeSelector; Applied InputType: Picker (display) -->
				{~T:PictProvider-Theme-Print-TabularTemplate-Begin-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-BridgeSelector',
			DefaultInputExtensions: [ 'PickerDisplay' ],
			Template: /*HTML-Partial*/`
				{~T:PictProvider-Theme-Print-TabularTemplate-End-Input-InputType-Picker~}`,
		},
	],
};

module.exports = _TemplatesGroupedByTheme;
